import { GeoshapeAdapter } from '@leverege/ui-google-maps-elements';
import { Attributes } from '@leverege/ui-attributes';
import { Selection } from '@leverege/ui-redux';
import Unit from '@leverege/unit';
import Model from "./GeoPointModel.js";
import Symbolizers from "../../shared/Symbolizers.js";
import Util from "./Util.js";
export default class GeoPointAdapter extends GeoshapeAdapter {
  constructor(opts) {
    super();
    this.Model = Model;
    this.attributeName = opts?.attributeName;
  }
  getShapeData(item, id, opts) {
    const options = opts || {};
    const {
      rollover,
      selected,
      symbols,
      model
    } = options;
    const isSelected = Selection.isSelected(selected, id);
    const isRollover = Selection.isSelected(rollover, id);
    let app = {
      visible: true,
      name: item?.data?.name,
      showSymbol: true,
      showLabel: false,
      showFill: false,
      showOutline: false,
      isSelected,
      isRollover
    };
    if (isSelected) {
      app.layerGroup = 'selected';
    }
    if (isRollover) {
      app.layerGroup = 'rollover';
    }
    const hasOverrideIconSize = model && this.Model.getHasOverrideIconSize(model);
    const overrideIconSize = model && this.Model.getOverrideIconSize(model);
    app.overrideIconSize = hasOverrideIconSize && overrideIconSize != null ? overrideIconSize : null;
    const llAttr = Attributes.getAttribute(this.attributeName, item.type);
    const ll = llAttr?.get(item.data);
    if (ll == null) {
      return null;
    }
    const subtype = Symbolizers.getSymbol(model.symbolizer, item, null);
    app.symbol = Array.isArray(subtype) ? symbols.getTypeSymbol(subtype[0] || item.type, subtype[1], app.isRollover, app.isSelected) : symbols.getTypeSymbol(item.type, subtype, app.isRollover, app.isSelected);
    const elevationOpts = llAttr.source.attribute?.metadata?.elevation;
    let elevation = 10;
    if (elevationOpts) {
      elevation = Attributes.get(elevationOpts.attribute, item.type, item.data);
      elevation = elevation?.unit ? Unit.convert(elevation.value, elevation.unit, 'm') : elevation;
    }
    const geoJson = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: [ll.lon, ll.lat, elevation]
      }
    };
    const accuracyOpts = llAttr.source.attribute?.metadata?.accuracy;
    const showAccuracyOn = model && Model.getShowAccuracyOn(model);
    const showAccuracy = showAccuracyOn && (showAccuracyOn === 'always' || showAccuracyOn === 'selected' && Selection.isSelected(selected, item.id) || showAccuracyOn === 'rollover' && Selection.isSelected(rollover, item.id));
    app.showAccuracy = showAccuracy;
    if (accuracyOpts && showAccuracy) {
      const {
        attribute,
        multiplier = 1
      } = accuracyOpts;
      const accuracyAttr = Attributes.getAttribute(attribute, item.type);
      const accuracy = accuracyAttr?.get(item.data);
      if (accuracy != null && (accuracyAttr.valueType === 'length' || accuracyAttr.valueType === 'number' || accuracyAttr.valueType === 'int')) {
        let accuracyValue = accuracy?.unit ? {
          ...accuracy,
          value: accuracy.value * multiplier
        } : {
          value: accuracy * multiplier,
          unit: accuracyOpts.unit || 'meters'
        };
        accuracyValue = Unit.convert(accuracyValue.value, accuracyValue.unit, 'm');
        app.accuracy = accuracyValue;
        const accuracyApp = this.getAccuracyAppearance(item, options);
        app = {
          ...accuracyApp,
          ...app
        };
      }
    }
    return {
      dataType: item.type,
      dataRef: item.ref,
      geoJson,
      appearance: app
    };
  }
  getSymbolLayerOptions(style, layerGroup, options) {
    const {
      model,
      zoom
    } = options;
    const rScale = model && this.Model.getRolloverScale(model) || 1;
    const sScale = model && this.Model.getSelectedScale(model) || 1;
    const nScale = model && this.Model.getNormalScale(model) || 1;
    const show = model && this.Model.getShowAtZoom(model);
    const hide = model && this.Model.getHideAtZoom(model);
    const hasOverrideIconSize = model && this.Model.getHasOverrideIconSize(model);
    const overrideIconSizeUnits = model && this.Model.getOverrideIconSizeUnits(model);
    const visible = Util.getVisible(show, hide, zoom);
    return {
      visible,
      pointType: 'circle+icon',
      iconSizeUnits: hasOverrideIconSize && overrideIconSizeUnits != null ? overrideIconSizeUnits : 'pixels',
      getPointRadius: d => {
        return d.properties.showAccuracy ? d.properties.accuracy : 0;
      },
      getIcon: d => {
        const symbol = d.properties.symbol;
        return {
          url: symbol.src,
          width: symbol.width,
          height: symbol.height || symbol.width
        };
      },
      getIconSize: d => {
        const overrideIconSize = d.properties.overrideIconSize;
        if (overrideIconSize) {
          return overrideIconSize;
        }
        const isRollover = d.properties.isRollover;
        const isSelected = d.properties.isSelected;
        const symbol = d.properties.symbol;
        let size = 0.5 * nScale;
        if (isRollover) {
          size = 0.5 * rScale;
        } else if (isSelected) {
          size = 0.5 * sScale;
        }
        return symbol.width * size;
      },
      pickable: model.rolloverable || model.selectable,
      filled: true,
      stroked: true,
      getFillColor: d => {
        const opacity = d.properties.fillOpacity != null ? d.properties.fillOpacity : 1;
        return Util.getColorAndOpacity(d.properties.fillColor, opacity);
      },
      getLineColor: d => {
        const opacity = d.properties.strokeOpacity != null ? d.properties.strokeOpacity : 1;
        return Util.getColorAndOpacity(d.properties.lineColor, opacity);
      },
      getLineWidth: d => {
        return d.properties.lineWidth || 1;
      }
    };
  }
  getAccuracyAppearance(item, {
    model
  }) {
    const app = {
      visible: true,
      name: `${item.id}.accuracy`,
      showSymbol: false,
      showLabel: false,
      showFill: true
    };
    const defaultColor = 'rgb(22, 97, 215)';
    app.fillColor = app.fillColor == null ? Util.getColor(Model.getFillColor(model), item) || defaultColor : app.fillColor;
    app.fillOpacity = app.fillOpacity == null ? Util.getNumber(Model.getFillOpacity(model), item) || 0.5 : app.fillOpacity;
    app.lineColor = app.lineColor == null ? Util.getColor(Model.getLineColor(model), item) || defaultColor : app.lineColor;
    app.lineOpacity = app.lineOpacity == null ? Util.getNumber(Model.getLineOpacity(model), item) || 1 : app.lineOpacity;
    app.lineStyle = app.lineStyle == null ? Model.getLineStyle(model) || 'default' : app.lineStyle;
    app.lineWidth = app.lineWidth == null ? Util.getNumber(Model.getLineWidth(model), item) : app.lineWidth;
    return app;
  }
}